import CheckboxFilter from 'components/checkbox-filter/checkbox-filter';
import ContentBody from 'components/content-body/content-body';
import ContentHeaderTitle from 'components/content-header-title/content-header-title';
import ContentHeader from 'components/content-header/content-header';
import Content from 'components/content/content';
import { ProFormCreateModal } from 'components/pro-form-create-modal';
import { ProPresentationSearchFilter } from 'components/pro-presentation-search-filter';
import { TradeSearchFilter } from 'components/trade-search-filter';
import { useCurrentBilikZones } from 'context/current-bilik-zones-context';
import { ProViewBoolExp } from 'generated/graphql';
import { useProViewsCount } from 'hooks/use-pro-views-count/use-pro-views-count';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Container,
  Grid,
  Icon,
  Loader,
  Pagination,
} from 'semantic-ui-react';
import styled from 'styled-components';
import {
  BooleanParam,
  NumberParam,
  ObjectParam,
  StringParam,
  useQueryParam,
  withDefault,
} from 'use-query-params';
import { ProsTable } from './pros-table';

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;

  > * {
    margin: 0 !important;
  }
`;

const ProsListPage: FunctionComponent = () => {
  const { currentBilikZones, loading: currentBilikZonesLoading } =
    useCurrentBilikZones();

  const itemPerPage = 20;

  const navigate = useNavigate();

  const [proFormCreateModalOpen, setProFormCreateModalOpen] = useState(false);
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [status, setStatus] = useQueryParam('status', StringParam);
  const [tradeId, setTradeId] = useQueryParam('tradeId', NumberParam);
  const [proPresentationId, setProPresentationId] = useQueryParam(
    'proPresentationId',
    NumberParam,
  );
  const [onHoliday, setOnHoliday] = useQueryParam(
    'onHoliday',
    withDefault(BooleanParam, undefined),
  );
  const [pendingContract, setPendingContract] = useQueryParam(
    'pendingContract',
    withDefault(BooleanParam, undefined),
  );
  const [archiveScheduled, setArchiveScheduled] = useQueryParam(
    'archiveScheduled',
    withDefault(BooleanParam, undefined),
  );

  const [search, setSearch] = useQueryParam('search', StringParam);

  const [onlyPresentationCategories, setOnlyPresentationCategories] =
    useState(true);

  const [orderBy, setOrderBy] = useQueryParam(
    'orderBy',
    withDefault(ObjectParam, { column: 'id', direction: 'descending' }),
  );

  const whereAll: ProViewBoolExp = useMemo(() => {
    const whereTrade: ProViewBoolExp | undefined = tradeId
      ? onlyPresentationCategories
        ? {
            proPresentation: {
              mainTrades: {
                tradeId: {
                  _eq: tradeId !== null ? tradeId : undefined,
                },
              },
            },
          }
        : {
            trades: {
              tradeId: {
                _eq: tradeId,
              },
            },
          }
      : undefined;

    const filters: ProViewBoolExp[] = [
      whereTrade,
      {
        proPresentationId: {
          _eq: proPresentationId !== null ? proPresentationId : undefined,
        },
      },
      {
        bilikZoneId: currentBilikZones
          ? {
              _in: currentBilikZones.map((zone) => zone.id),
            }
          : undefined,
      },
      onHoliday ? { activeHoliday: {} } : {},
      pendingContract
        ? {
            status: { _in: ['draft', 'published'] },
            // Paper contracts are uploaded to S3 only: no configuration in DB
            contractConfiguration: { _isNull: false },
            _not: {
              _and: [
                { contractEvents: { _contains: [{ type: 'ProSigned' }] } },
                { contractEvents: { _contains: [{ type: 'BilikSigned' }] } },
              ],
            },
          }
        : {},
      // Sub-filter of the published pros: only active when it is displayed
      archiveScheduled && (!status || status === 'published')
        ? { dateArchiveScheduled: { _gt: new Date().toISOString() } }
        : {},
    ].filter((filter): filter is ProViewBoolExp => filter !== undefined);

    return {
      _and: filters,
    };
  }, [
    proPresentationId,
    tradeId,
    search,
    currentBilikZones,
    onlyPresentationCategories,
    onHoliday,
    pendingContract,
    archiveScheduled,
    status,
  ]);

  const whereTable: ProViewBoolExp = useMemo(() => {
    return {
      ...whereAll,
      status: { _eq: status !== null ? status : undefined },
    };
  }, [whereAll, status]);

  const whereIsStatusPublished: ProViewBoolExp = useMemo(() => {
    return {
      ...whereAll,
      status: { _eq: 'published' },
    };
  }, [whereAll]);

  const whereIsStatusDraft: ProViewBoolExp = useMemo(() => {
    return {
      ...whereAll,
      status: { _eq: 'draft' },
    };
  }, [whereAll]);

  const whereIsStatusArchived: ProViewBoolExp = useMemo(() => {
    return {
      ...whereAll,
      status: { _eq: 'archived' },
    };
  }, [whereAll]);

  const { count: countAll } = useProViewsCount(whereAll, search ?? undefined);

  const { count: countTable } = useProViewsCount(
    whereTable,
    search ?? undefined,
  );

  const totalPages = useMemo(
    () => Math.ceil((countTable || 0) / itemPerPage),
    [countTable],
  );

  const { count: countIsStatusPublished, loading: loadingIsStatusPublished } =
    useProViewsCount(whereIsStatusPublished, search ?? undefined);

  const { count: countIsStatusDraft, loading: loadingIsStatusDraft } =
    useProViewsCount(whereIsStatusDraft, search ?? undefined);

  const { count: countIsStatusArchived, loading: loadingIsStatusArchived } =
    useProViewsCount(whereIsStatusArchived, search ?? undefined);

  if (currentBilikZonesLoading || currentBilikZones === undefined) {
    return (
      <Content>
        <Loader
          style={{ marginTop: '50px' }}
          size="large"
          active
          inline="centered"
        >
          Chargement...
        </Loader>
      </Content>
    );
  }

  return (
    <Content>
      <ContentHeader>
        <Grid columns={2}>
          <Grid.Column>
            <ContentHeaderTitle>
              Fiches pros
              <small>
                {currentBilikZones
                  ? currentBilikZones
                      .map((zone) => zone.mainCityCode)
                      .join(', ')
                  : 'Toutes les zones'}
              </small>
            </ContentHeaderTitle>
          </Grid.Column>
          <Grid.Column textAlign="right">
            <Button
              positive
              type="button"
              onClick={(): void => {
                setProFormCreateModalOpen(true);
              }}
            >
              <Icon name="plus" />
              Nouveau
            </Button>
            <ProFormCreateModal
              open={proFormCreateModalOpen}
              onClose={(): void => {
                setProFormCreateModalOpen(false);
              }}
              onProCreated={(proViewId): void => {
                navigate(`/pros/update/${proViewId}`);
              }}
            />
          </Grid.Column>
        </Grid>
      </ContentHeader>
      <ContentBody>
        <Filters>
          <FiltersRow>
            <Button.Group>
              <Button
                basic={status !== 'published'}
                color="green"
                loading={loadingIsStatusPublished}
                onClick={(): void => {
                  setStatus('published');
                  setPage(1);
                }}
              >
                Publiés ({countIsStatusPublished ?? 0})
              </Button>
              <Button
                basic={status !== 'draft'}
                color="red"
                loading={loadingIsStatusDraft}
                onClick={(): void => {
                  setStatus('draft');
                  setPage(1);
                }}
              >
                Brouillon ({countIsStatusDraft ?? 0})
              </Button>
              <Button
                basic={status !== 'archived'}
                color="grey"
                loading={loadingIsStatusArchived}
                onClick={(): void => {
                  setStatus('archived');
                  setPage(1);
                }}
              >
                Archivés ({countIsStatusArchived ?? 0})
              </Button>
              <Button
                basic={status !== undefined}
                color="grey"
                onClick={(): void => {
                  setStatus(undefined);
                  setPage(1);
                }}
              >
                Tous ({countAll ?? 0})
              </Button>
            </Button.Group>

            <ProPresentationSearchFilter
              proPresentationId={
                proPresentationId !== null ? proPresentationId : undefined
              }
              setProPresentationId={setProPresentationId}
              bilikZoneIds={currentBilikZones?.map((zone) => zone.id)}
              setPage={setPage}
              search={true}
              initialValue={search ?? undefined}
              setSearch={setSearch}
            />
            <TradeSearchFilter
              tradeId={tradeId || undefined}
              onDelete={(): void => {
                setTradeId(undefined);
                setPage(1);
              }}
              onResultSelect={(event, { result }): void => {
                setTradeId(result.id);
                setPage(1);
              }}
            />
            {tradeId && (
              <CheckboxFilter
                label="Catégories principales uniquement"
                className="button basic grey"
                checked={onlyPresentationCategories}
                onChange={(): void => {
                  setOnlyPresentationCategories(!onlyPresentationCategories);
                }}
              />
            )}
          </FiltersRow>
          <FiltersRow>
            {(!status || status === 'published') && (
              <CheckboxFilter
                checked={archiveScheduled}
                className="button basic grey"
                label="Archivage programmé"
                onChange={(): void => {
                  setArchiveScheduled(!archiveScheduled);
                  setPage(1);
                }}
              />
            )}
            <CheckboxFilter
              checked={onHoliday}
              className="button basic grey"
              label="En congé"
              onChange={(): void => {
                setOnHoliday(!onHoliday);
              }}
            />
            <CheckboxFilter
              checked={pendingContract}
              className="button basic grey"
              label="Contrat à signer"
              onChange={(): void => {
                setPendingContract(!pendingContract);
                setPage(1);
              }}
            />
          </FiltersRow>
        </Filters>
        <ProsTable
          search={search ?? ''}
          where={whereTable}
          page={page ?? undefined}
          setPage={setPage}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          itemPerPage={itemPerPage}
          selectedTradeId={tradeId}
        />
        {totalPages > 1 ? (
          <Container fluid textAlign="right" style={{ marginTop: '15px' }}>
            <Pagination
              activePage={page ?? 1}
              ellipsisItem={{
                content: <Icon name="ellipsis horizontal" />,
                icon: true,
              }}
              firstItem={{
                content: <Icon name="angle double left" />,
                icon: true,
              }}
              lastItem={{
                content: <Icon name="angle double right" />,
                icon: true,
              }}
              prevItem={{ content: <Icon name="angle left" />, icon: true }}
              nextItem={{ content: <Icon name="angle right" />, icon: true }}
              totalPages={totalPages}
              onPageChange={(_, data): void => {
                setPage(Number(data.activePage));
              }}
            />
          </Container>
        ) : null}
      </ContentBody>
    </Content>
  );
};

export default ProsListPage;
